/**
 * Where to get it, and what each platform will put in your way.
 *
 * The macOS installers are signed and notarized from 0.46.5, so the card says so
 * and the install guide has no Gatekeeper step left to warn about.
 */

import { Link } from 'react-router-dom';
import { useI18n, useLocalizedPath } from '@/lib/i18n';
import { PageHead, Section, Table } from '@/components/site/Page';
import '@/components/site/Page.scss';
import { DOWNLOADS, MEETING_SITES_DETAIL } from '@/components/site/homeSections';
import './Download.scss';

const CHROME_STORE =
  'https://chromewebstore.google.com/detail/sokuji-extension/ppmihnhelgfpjomhjhpecobloelicnak';
const EDGE_STORE =
  'https://microsoftedge.microsoft.com/addons/detail/sokuji-ai-meeting-trans/dcmmcdkeibkalgdjlahlembodjhijhkm';
/**
 * The web listing rather than `ms-windows-store://`, which a Mac or Linux
 * browser cannot open. `referrer` and `source` are the ones Microsoft's own
 * badge script adds, so Partner Center counts these visits as badge traffic.
 */
const MS_STORE =
  'https://apps.microsoft.com/detail/XP8M190BGC8NF3?referrer=appbadge&source=sokuji.kizuna.ai';
/** Microsoft's badge artwork, unmodified, one per site locale (the 12 Store-supplied languages). */
const MS_STORE_BADGES = '/badges/microsoft-store';
const MACOS_GUIDE = '/docs/install/macos';

/**
 * The three desktop cards, in the order most visitors arrive on them. No
 * platform is marked out: this page is where someone finds their own build,
 * not where we rank the three.
 */
const DESKTOP = ['windows', 'macos', 'linux'] as const;

export function Download() {
  const { t, locale } = useI18n();
  const lp = useLocalizedPath();

  return (
    <div className="site-page download">
      <PageHead
        kicker={t('nav.download')}
        title={t('download.page.title')}
        lead={t('download.page.lead')}
      />

      <Section title={t('download.desktop.title')} lead={t('download.desktop.lead')}>
        <div className="download__cards">
          {DESKTOP.map((id) => (
            <article key={id} id={id} className="download__card">
              <h3>{t(`download.${id}.title`)}</h3>
              <p>
                {id === 'macos' ? (
                  // The two words the card turns on; the design sets them apart
                  // rather than leaving them in the middle of a sentence.
                  <>
                    {t('download.macos.bodyBefore')}{' '}
                    <strong>{t('download.macos.signed')}</strong>
                    {t('download.macos.bodyAfter')}
                  </>
                ) : (
                  t(`download.${id}.body`)
                )}
              </p>

              {/* Every build, not just the one we would pick for you: an arm64
                  laptop and an Intel Mac are both somebody's only option. */}
              <div className="site-label download__label download__label--builds">
                {t('download.builds')}
              </div>
              <ul className="download__builds">
                {DOWNLOADS[id].map(([label, href]) => (
                  <li key={href}>
                    <a href={href} className="site-code">
                      {label}
                    </a>
                  </li>
                ))}
              </ul>

              {/* The Store installs the same build, but its copy is only as
                  new as our last submission, so the direct installer stays
                  first. Both badges are in the markup and the theme picks one:
                  the prerendered page cannot know the visitor's theme. */}
              {id === 'windows' && (
                <a
                  href={MS_STORE}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="download__store"
                >
                  <img
                    src={`${MS_STORE_BADGES}/${locale}-dark.svg`}
                    alt="Microsoft Store"
                    height={44}
                    className="download__store-badge download__store-badge--dark"
                  />
                  <img
                    src={`${MS_STORE_BADGES}/${locale}-light.svg`}
                    alt="Microsoft Store"
                    height={44}
                    className="download__store-badge download__store-badge--light"
                  />
                </a>
              )}

              {/* At the foot of the card, so the three caveats sit on one line
                  across the row: the copy above runs to different lengths and
                  the build lists are one, two and four rows long. */}
              <div className="site-label download__note">
                {id === 'macos' ? (
                  <>
                    {t('download.macos.noteLead')}{' '}
                    <Link to={lp(MACOS_GUIDE)}>{t('download.readGuide')}</Link>
                  </>
                ) : (
                  t(`download.${id}.note`)
                )}
              </div>
            </article>
          ))}
        </div>
      </Section>

      <Section id="extension" title={t('download.ext.title')} lead={t('download.ext.lead')}>
        <div className="download__ext">
          <article className="download__card">
            <h3>{t('download.ext.browser')}</h3>
            <p>{t('download.ext.browserBody')}</p>
            <div className="download__actions download__actions--pair">
              <a
                href={CHROME_STORE}
                target="_blank"
                rel="noopener noreferrer"
                className="download__cta download__cta--primary"
              >
                {t('download.ext.addChrome')}
              </a>
              <a
                href={EDGE_STORE}
                target="_blank"
                rel="noopener noreferrer"
                className="download__cta"
              >
                {t('download.ext.addEdge')}
              </a>
            </div>
          </article>

          <article className="download__card">
            <div className="site-label download__label">{t('download.ext.worksOn')}</div>
            <p className="download__sites">{MEETING_SITES_DETAIL.join('  ·  ')}</p>
            <div className="site-label download__note">{t('download.ext.elsewhere')}</div>
          </article>
        </div>
      </Section>

      <Section title={t('download.need.title')}>
        <Table
          head={['', t('download.need.col')]}
          widths="0.5fr 2fr"
          rows={[
            [
              'Linux',
              <>
                {t('download.need.linuxBefore')} <code className="site-code">pulseaudio-utils</code>{' '}
                {t('download.need.linuxMid')} <code className="site-code">pactl</code>
              </>,
            ],
            ['Windows', t('download.need.windows')],
            ['macOS', t('download.need.macos')],
            [t('download.need.any'), t('download.need.anyBody')],
            [t('download.need.nothing'), t('download.need.nothingBody')],
          ]}
        />
      </Section>
    </div>
  );
}
